(function() {
    'use strict';
    
    class ExecutiveGrid {
        constructor(container) {
            this.container = container;
            this.selectedExecutive = null;
            this.executiveData = [];
            this.resizeTimeout = null;
            this.breakpoints = {
                lg: { min: 1200, itemsPerRow: 5 },
                md: { min: 768, max: 1199, itemsPerRow: 2 },
                sm: { max: 767, itemsPerRow: 1 }
            };
            
            this.init();
        }
        
        init() {
            this.loadExecutiveData();
            this.bindEvents();
        }
        
        loadExecutiveData() {
            const dataElement = this.findDataElement();
            
            if (dataElement) {
                try {
                    this.executiveData = JSON.parse(dataElement.textContent);
                } catch (e) {
                    console.error('Failed to parse executive data:', e);
                }
            } else {
                console.warn('No executive data found for container:', this.container);
            }
        }
        
        findDataElement() {
            const strategies = [
                () => this.container.querySelector('[id*="executive-data"]'),
                () => {
                    const blockId = this.container.id;
                    return blockId ? document.getElementById(`executive-data-${blockId}`) : null;
                },
                () => document.getElementById('executive-data'),
                () => this.findSiblingDataElement()
            ];
            
            for (const strategy of strategies) {
                const element = strategy();
                if (element) return element;
            }
            
            return null;
        }
        
        findSiblingDataElement() {
            let nextElement = this.container.nextElementSibling;
            while (nextElement) {
                if (nextElement.id && nextElement.id.includes('executive-data')) {
                    return nextElement;
                }
                nextElement = nextElement.nextElementSibling;
            }
            return null;
        }
        
        bindEvents() {
            const expanderButtons = this.container.querySelectorAll('.executive-card .js-expander');
            
            this.boundClickHandler = (e) => {
                e.preventDefault();
                const card = e.target.closest('.executive-card');
                const executiveId = parseInt(card.dataset.executiveId);
                this.handleExecutiveClick(executiveId);
            };
            
            this.boundResizeHandler = () => {
                clearTimeout(this.resizeTimeout);
                this.resizeTimeout = setTimeout(() => {
                    if (this.selectedExecutive !== null) {
                        this.showAccordionForExecutive(this.selectedExecutive);
                    }
                }, 250);
            };
            
            expanderButtons.forEach(button => {
                button.addEventListener('click', this.boundClickHandler);
            });
            
            window.addEventListener('resize', this.boundResizeHandler);
        }
        
        handleExecutiveClick(executiveId) {
            if (this.selectedExecutive === executiveId) {
                this.selectedExecutive = null;
                this.hideAllAccordions();
            } else {
                this.selectedExecutive = executiveId;
                this.showAccordionForExecutive(executiveId);
            }
        }
        
        hideAllAccordions() {
            const accordions = this.container.querySelectorAll('.executive-accordion');
            accordions.forEach(accordion => accordion.classList.remove('active'));
        }
        
        getCurrentLayout() {
            const screenWidth = window.innerWidth;
            
            if (screenWidth >= this.breakpoints.lg.min) {
                return { type: 'lg', itemsPerRow: this.breakpoints.lg.itemsPerRow };
            } else if (screenWidth >= this.breakpoints.md.min) {
                return { type: 'md', itemsPerRow: this.breakpoints.md.itemsPerRow };
            } else {
                return { type: 'sm', itemsPerRow: this.breakpoints.sm.itemsPerRow };
            }
        }
        
        calculateRowPosition(executiveId, layout) {
            const rowIndex = Math.floor(executiveId / layout.itemsPerRow);
            const positionInRow = executiveId % layout.itemsPerRow;
            
            return { rowIndex, positionInRow };
        }
        
        showAccordionForExecutive(executiveId) {
            this.hideAllAccordions();
            
            const layout = this.getCurrentLayout();
            const { rowIndex, positionInRow } = this.calculateRowPosition(executiveId, layout);
            
            const rowWrapper = this.container.querySelector(
                `.executive-layout-${layout.type} .executive-row-wrapper[data-row="${rowIndex}"]`
            );
            
            if (rowWrapper) {
                const accordion = rowWrapper.querySelector(`.${layout.type}-accordion`);
                
                if (accordion) {
                    accordion.classList.add('active');
                    requestAnimationFrame(() => {
                        this.setupAccordionContent(accordion, executiveId, positionInRow, layout.type);
                    });
                }
            }
        }
        
        setupAccordionContent(accordion, executiveId, positionInRow, layoutType) {
            if (!this.executiveData[executiveId]) {
                console.warn('No data found for executive ID:', executiveId);
                return;
            }
            
            const executive = this.executiveData[executiveId];
            const caret = accordion.querySelector('.accordion-caret');
            
            if (caret) {
                caret.setAttribute('data-position', positionInRow);
                caret.setAttribute('data-layout', layoutType);
                caret.style.left = this.calculateCaretPosition(accordion, executiveId) + '%';
            }
            
            this.setAccordionContent(accordion, executive);
        }
        
        calculateCaretPosition(accordion, executiveId) {
            const rowWrapper = accordion.closest('.executive-row-wrapper');
            const cardsContainer = rowWrapper.querySelector('.executive-cards-flex');
            const targetCard = cardsContainer.querySelector(`[data-executive-id="${executiveId}"]`);
            
            if (!targetCard || !cardsContainer) return 50;
            
            cardsContainer.offsetHeight; // Force reflow
            
            const accordionContent = accordion.querySelector('.accordion-content');
            const accordionRect = accordionContent.getBoundingClientRect();
            const cardRect = targetCard.getBoundingClientRect();
            
            const cardCenterX = cardRect.left + (cardRect.width / 2) - accordionRect.left;
            const caretPosition = (cardCenterX / accordionRect.width) * 100;
            
            return Math.max(5, Math.min(95, caretPosition));
        }
        
        setAccordionContent(accordion, executive) {
            const content = accordion.querySelector('.accordion-inner');
            if (!content) return;
            
            const biography = executive.work_biography || executive.bio || executive.biography || '';
            content.innerHTML = biography;
        }
        
        cleanup() {
            const expanderButtons = this.container.querySelectorAll('.executive-card .js-expander');
            expanderButtons.forEach(button => {
                button.removeEventListener('click', this.boundClickHandler);
            });
            
            window.removeEventListener('resize', this.boundResizeHandler);
            
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            
            this.selectedExecutive = null;
        }
    }
    
    function initializeExecutiveGrids() {
        const containers = document.querySelectorAll('.executive-grid-container, .block-executive-grid');
        const instances = [];
        
        containers.forEach(container => {
            if (container.querySelector('.executive-card')) {
                instances.push(new ExecutiveGrid(container));
            }
        });
        
        window.executiveGridInstances = instances;
    }
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeExecutiveGrids);
    } else {
        initializeExecutiveGrids();
    }
    
    window.executiveGridCleanup = function() {
        if (window.executiveGridInstances) {
            window.executiveGridInstances.forEach(instance => instance.cleanup());
            window.executiveGridInstances = [];
        }
    };
    
})();